import { useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import { cn } from "../../lib/cn";
import type { PreviewDataset } from "../../lib/api";
import type { TagDrawerInfo } from "./types";
import { TAG_PILL_COLORS } from "./types";
import { CloseIcon } from "./icons";

export function DrawerSection({ title, count, children }: { title: string; count?: number; children: ReactNode }) {
  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <span className="shrink-0 font-mono text-[10.5px] font-semibold uppercase tracking-[0.16em] text-ash">
          {title}
        </span>
        {count !== undefined && (
          <span className="shrink-0 font-mono text-[10.5px] text-ash/50">{count}</span>
        )}
        <div className="h-px flex-1 bg-rule" />
      </div>
      {children}
    </div>
  );
}

export function DrawerKVTable({ userProps, systemProps }: { userProps: [string, string][]; systemProps: [string, string][] }) {
  return (
    <div className="divide-y divide-rule/60 overflow-hidden rounded-md border border-rule">
      {userProps.map(([k, v]) => (
        <div key={k} className="flex items-start gap-4 bg-white px-4 py-2.5 transition-colors hover:bg-paper/60">
          <span className="w-44 shrink-0 text-[12px] font-medium text-ash">{k}</span>
          <span className="text-[12px] text-ink break-all">{v}</span>
        </div>
      ))}
      {systemProps.length > 0 && (
        <>
          <div className="bg-paper/40 px-4 py-1.5">
            <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-ash/50">System</span>
          </div>
          {systemProps.map(([k, v]) => (
            <div key={k} className="flex items-start gap-4 bg-white px-4 py-2.5 transition-colors hover:bg-paper/60">
              <span className="w-44 shrink-0 text-[12px] font-medium text-ash/50">{k}</span>
              <span className="text-[12px] text-ash/60 break-all">{v}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

export function AssetPropertiesDrawer({
  dataset: d,
  onClose,
}: {
  dataset: PreviewDataset | null;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!d) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!d]);

  const userProps = useMemo(
    () => Object.entries(d?.properties ?? {}).filter(([k]) => !k.startsWith("__")).sort(([a], [b]) => a.localeCompare(b)),
    [d],
  );
  const systemProps = useMemo(
    () => Object.entries(d?.properties ?? {}).filter(([k]) => k.startsWith("__")).sort(([a], [b]) => a.localeCompare(b)),
    [d],
  );

  const hasProps = userProps.length > 0 || systemProps.length > 0;
  const hasStructured = (d?.structuredProps?.length ?? 0) > 0;
  const hasLabels = (d?.tags?.length ?? 0) > 0;
  const tagCount = d?.fieldsTotal ?? d?.schema?.length ?? 0;

  return (
    <>
      <div
        onClick={onClose}
        className={cn(
          "fixed inset-0 z-[29] bg-black/30 transition-opacity duration-300",
          d ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
        )}
      />
      <div
        className={cn(
          "fixed top-0 bottom-0 right-0 z-50 flex w-[480px] flex-col border-l border-rule bg-white shadow-2xl transition-transform duration-300 ease-out",
          d ? "translate-x-0" : "translate-x-full",
        )}
      >
        {d && (
          <>
            <div className="flex-shrink-0 border-b border-rule bg-white px-6 pb-5 pt-[76px]">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.16em] text-ash">
                    Asset Properties
                  </p>
                  <h2 className="mt-1.5 font-display text-[21px] font-semibold leading-tight tracking-tight text-ink">
                    {d.name || "—"}
                  </h2>
                  <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                    <span className="chip-neutral text-[11px]">{d.platform}</span>
                    {d.subTypes?.map((t) => (
                      <span key={t} className="chip-brand text-[11px]">{t}</span>
                    ))}
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="mt-0.5 shrink-0 rounded-md p-1.5 text-ash hover:text-ink hover:bg-rule transition-colors"
                  aria-label="Close"
                >
                  <CloseIcon />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="space-y-7 px-6 py-6">
                {d.description && (
                  <DrawerSection title="Description">
                    <p className="text-[13.5px] leading-relaxed text-ash">{d.description}</p>
                  </DrawerSection>
                )}

                <DrawerSection title="Tags / Schema">
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-[30px] font-semibold tabular-nums text-ink">
                      {tagCount.toLocaleString()}
                    </span>
                    <span className="text-[13px] text-ash">{tagCount === 1 ? "tag" : "tags"} in this asset</span>
                  </div>
                  {d.fieldsTruncated && (
                    <p className="mt-1.5 text-[11.5px] text-amber-700">
                      Preview shows the first 500 tags. All {tagCount.toLocaleString()} will be ingested on the full run.
                    </p>
                  )}
                </DrawerSection>

                {hasLabels && (
                  <DrawerSection title="Labels" count={d.tags!.length}>
                    <div className="flex flex-wrap gap-1.5">
                      {d.tags!.map((t, i) => (
                        <span key={t} className={cn("inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-medium", TAG_PILL_COLORS[i % TAG_PILL_COLORS.length])}>
                          {t}
                        </span>
                      ))}
                    </div>
                  </DrawerSection>
                )}

                {hasProps && (
                  <DrawerSection title="Properties" count={userProps.length + systemProps.length}>
                    <DrawerKVTable userProps={userProps} systemProps={systemProps} />
                  </DrawerSection>
                )}

                {hasStructured && (
                  <DrawerSection title="Device Attributes" count={d.structuredProps!.length}>
                    <div className="divide-y divide-rule/60 overflow-hidden rounded-md border border-rule">
                      {d.structuredProps!.map((p, i) => (
                        <div key={i} className="flex items-start gap-4 bg-white px-4 py-2.5 transition-colors hover:bg-paper/60">
                          <span className="w-44 shrink-0 text-[12px] font-medium text-ash">
                            {p.propertyId.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                          </span>
                          <span className="text-[12px] text-ink break-all">
                            {p.values.map((v) => String(v)).join(", ")}
                          </span>
                        </div>
                      ))}
                    </div>
                  </DrawerSection>
                )}

                {!d.description && !hasLabels && !hasProps && !hasStructured && (
                  <div className="rounded-lg border border-rule/60 bg-paper px-4 py-8 text-center">
                    <p className="text-[13px] text-ash">No additional properties for this asset.</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export function TagPropertiesDrawer({
  tagInfo,
  onClose,
}: {
  tagInfo: TagDrawerInfo | null;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!tagInfo) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!tagInfo]);

  const field = tagInfo?.field;
  const hasStructured = (field?.structuredProps?.length ?? 0) > 0;
  const hasFieldLabels = (field?.tags?.length ?? 0) > 0;

  return (
    <>
      <div
        onClick={onClose}
        className={cn(
          "fixed inset-0 z-[29] bg-black/30 transition-opacity duration-300",
          tagInfo ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
        )}
      />
      <div
        className={cn(
          "fixed top-0 bottom-0 right-0 z-50 flex w-[440px] flex-col border-l border-rule bg-white shadow-2xl transition-transform duration-300 ease-out",
          tagInfo ? "translate-x-0" : "translate-x-full",
        )}
      >
        {tagInfo && field && (
          <>
            <div className="flex-shrink-0 border-b border-rule bg-white px-6 pb-5 pt-[76px]">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.16em] text-ash">
                    {tagInfo.assetName}
                    <span className="mx-1.5 text-rule">›</span>
                    Tag
                  </p>
                  <h2 className="mt-1.5 font-display text-[18px] font-semibold leading-tight tracking-tight text-ink break-all">
                    {field.path}
                  </h2>
                  <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                    <span className="chip-neutral text-[11px]">{field.nativeType}</span>
                    {field.nullable && <span className="chip-neutral text-[11px]">Nullable</span>}
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="mt-0.5 shrink-0 rounded-md p-1.5 text-ash hover:text-ink hover:bg-rule transition-colors"
                  aria-label="Close"
                >
                  <CloseIcon />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="space-y-7 px-6 py-6">
                {field.description && (
                  <DrawerSection title="Description">
                    <p className="text-[13.5px] leading-relaxed text-ash">{field.description}</p>
                  </DrawerSection>
                )}

                {hasFieldLabels && (
                  <DrawerSection title="Labels" count={field.tags!.length}>
                    <div className="flex flex-wrap gap-1.5">
                      {field.tags!.map((t, i) => (
                        <span key={t} className={cn("inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-medium", TAG_PILL_COLORS[i % TAG_PILL_COLORS.length])}>
                          {t}
                        </span>
                      ))}
                    </div>
                  </DrawerSection>
                )}

                {hasStructured && (
                  <DrawerSection title="Properties" count={field.structuredProps!.length}>
                    <div className="divide-y divide-rule/60 overflow-hidden rounded-md border border-rule">
                      {field.structuredProps!.map((p, i) => (
                        <div key={i} className="flex items-start gap-4 bg-white px-4 py-2.5 transition-colors hover:bg-paper/60">
                          <span className="w-44 shrink-0 text-[12px] font-medium text-ash">
                            {p.propertyId.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                          </span>
                          <span className="text-[12px] text-ink break-all">
                            {p.values.map((v) => String(v)).join(", ")}
                          </span>
                        </div>
                      ))}
                    </div>
                  </DrawerSection>
                )}

                {!field.description && !hasFieldLabels && !hasStructured && (
                  <div className="rounded-lg border border-rule/60 bg-paper px-4 py-8 text-center">
                    <p className="text-[13px] text-ash">No additional properties for this tag.</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
